/**
 * The words the AI starts a call with, and what it offers, on the "AI calls
 * out" tab.
 *
 * The opening is five parts and the owner may change two of them: the reason
 * we call (one sentence per contact the company had with us, and on the
 * settings page up to four more VARIANTS per contact, said in turn) and the
 * question at the end. A reason sentence may name the company's most important
 * finding from the development brain with `{viga}`; a call only says such a
 * sentence to a company that has one. The AI disclosure, the recording notice, the refusal sentence and
 * the source sentence on a mobile are fixed, and the preview shows them in
 * place, so what is typed is always read inside the sentence it lands in.
 *
 * The preview is the server's (`previewOpening`), asked a moment after typing
 * stops: the same builder and the same check a call uses. A problem it finds
 * is shown under the field that caused it, and the save refuses the same.
 *
 * An AI call campaign edits the same four texts (`ai-calls/campaigns/`). There a
 * blank text means the SETTINGS' text rather than the built-in one, so the
 * campaign passes those as `fallback` and the preview reads a blank field the
 * way a call would.
 */
import { useEffect, useRef, useState } from "react";
import { AlertTriangle, Loader2, Plus, Trash2 } from "lucide-react";
import { Button } from "@wa/ui";
import { useT } from "../../../../../i18n/I18nProvider";
import { OUTBOUND_GOALS, OUTBOUND_TEXT_LIMITS, type OpeningReasonSlot } from "../../../../../lib/aiOutbound";
import {
  FINDING_PLACEHOLDER,
  OPENING_PLACEHOLDERS,
  blankExtras,
  extraServerIndex,
  openingPreviewKey,
  openingProblemKey,
  previewOpening,
  problemsFor,
  withPlaceholder,
  type OpeningDraft,
  type OpeningPreview,
} from "../../../../../lib/aiOutboundOpening";
import { PillTabs, SelectPill } from "../../../../ui";
import { Field, FieldBox, FieldGroup, Note, Pill, ToggleRow } from "../../panel/fields";

/** How long typing must pause before the preview is asked for. */
const PREVIEW_DELAY_MS = 400;

export function AiOutboundOpening({
  draft,
  onChange,
  defaults,
  fallback,
  disabled,
  mode = "settings",
}: {
  draft: OpeningDraft;
  onChange: (next: OpeningDraft) => void;
  /** The sentences a blank field stands for, shown as placeholders. */
  defaults: { question: string; reasons: Record<OpeningReasonSlot, string> };
  /** Texts a blank field falls back to before the built-in ones (a campaign over the settings). */
  fallback?: { question: string; reasons: Record<OpeningReasonSlot, string> };
  disabled?: boolean;
  /** "campaign": a blank field is the settings' text, and the goal is the campaign's, not a test call's. */
  mode?: "settings" | "campaign";
}) {
  const t = useT();
  // The three labels whose meaning moves with the variant; everything else is shared.
  const own = mode === "campaign" ? "aiCalls.campaigns.opening" : "settings.aiOutbound.opening";
  const [slot, setSlot] = useState<OpeningReasonSlot>("cold");
  // 0 is the slot's first sentence, 1 and up its extra sentences. A campaign has only the first.
  const [variantAt, setVariantAt] = useState(0);
  const [mobile, setMobile] = useState(false);
  const [preview, setPreview] = useState<OpeningPreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const request = useRef(0);
  const sent = fallback
    ? {
        reasons: {
          letter: draft.reasons.letter.trim() || fallback.reasons.letter,
          call: draft.reasons.call.trim() || fallback.reasons.call,
          cold: draft.reasons.cold.trim() || fallback.reasons.cold,
        },
        question: draft.question.trim() || fallback.question,
      }
    : draft;
  const extras = draft.extras;
  const slotExtras = extras?.[slot] ?? [];
  // A variant removed in another slot, or a slot switch, may leave the index past the end.
  const at = Math.min(variantAt, slotExtras.length);
  const key = openingPreviewKey(sent);

  useEffect(() => {
    // Read-only (a campaign seen without the manage right): the preview route
    // would refuse, and a "could not preview" note would read as a fault.
    if (disabled) {
      setLoading(false);
      return;
    }
    const id = ++request.current;
    setLoading(true);
    const timer = setTimeout(() => {
      previewOpening({
        openingReasons: sent.reasons,
        ...(extras ? { extraOpeningReasons: extras } : {}),
        openingQuestion: sent.question,
      })
        .then((next) => {
          if (id !== request.current) return;
          setPreview(next);
          setFailed(false);
        })
        .catch(() => {
          if (id === request.current) setFailed(true);
        })
        .finally(() => {
          if (id === request.current) setLoading(false);
        });
    }, PREVIEW_DELAY_MS);
    return () => clearTimeout(timer);
    // `key` stands for the reasons, their variants and the question: the preview depends on nothing else.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, disabled]);

  // The preview and the problems name an extra sentence by its place in the list the server keeps;
  // -1 is an extra the server drops (blank or a repeat), which has no opening of its own.
  const extraAt = at === 0 ? 0 : extraServerIndex(slotExtras, at - 1);
  const serverAt = at === 0 ? 0 : extraAt < 0 ? -1 : extraAt + 1;
  const variant =
    serverAt < 0 ? null : (preview?.variants.find((v) => v.slot === slot && (v.index ?? 0) === serverAt) ?? null);
  const text = variant ? (mobile ? variant.mobileText : variant.text) : "";
  const seconds = variant ? (mobile ? variant.mobileSeconds : variant.seconds) : 0;
  const reasonField = at === 0 ? `openingReasons.${slot}` : `extraOpeningReasons.${slot}.${serverAt - 1}`;
  const reasonProblems = serverAt > 0 || at === 0 ? problemsFor(preview, reasonField) : [];
  const reasonValue = at === 0 ? draft.reasons[slot] : (slotExtras[at - 1] ?? "");
  const variantTabs = [0, ...slotExtras.map((_, i) => i + 1)].map((i) => ({
    value: String(i),
    label: t("settings.aiOutbound.opening.variant", { n: i + 1 }),
  }));
  const questionProblems = problemsFor(preview, "openingQuestion");
  const slotTabs = (["cold", "letter", "call"] as const).map((value) => ({
    value,
    label: t(`settings.aiOutbound.opening.slot.${value}`),
  }));

  function setReason(value: string) {
    if (at === 0 || !extras) {
      onChange({ ...draft, reasons: { ...draft.reasons, [slot]: value } });
      return;
    }
    const list = slotExtras.map((old, i) => (i === at - 1 ? value : old));
    onChange({ ...draft, extras: { ...extras, [slot]: list } });
  }

  function addVariant() {
    const next = extras ?? blankExtras();
    if (slotExtras.length >= OUTBOUND_TEXT_LIMITS.extraReasons) return;
    onChange({ ...draft, extras: { ...next, [slot]: [...slotExtras, ""] } });
    setVariantAt(slotExtras.length + 1);
  }

  function removeVariant() {
    if (!extras || at === 0) return;
    onChange({ ...draft, extras: { ...extras, [slot]: slotExtras.filter((_, i) => i !== at - 1) } });
    setVariantAt(at - 1);
  }

  const problemText = (codes: string[]) =>
    codes.map((code) => t(`aiOutbound.opening.problem.${openingProblemKey(code)}`)).join(", ");

  return (
    <FieldGroup title={t("settings.aiOutbound.opening.group")}>
      <Note tone="info">{t("settings.aiOutbound.opening.fixedNote")}</Note>

      <FieldBox label={t("settings.aiOutbound.opening.slotLabel")} hint={t(`settings.aiOutbound.opening.slotHint.${slot}`)}>
        <PillTabs
          tabs={slotTabs}
          value={slot}
          onChange={(next) => {
            setSlot(next);
            setVariantAt(0);
          }}
          small
        />
      </FieldBox>

      {extras ? (
        <FieldBox label={t("settings.aiOutbound.opening.variants")} hint={t("settings.aiOutbound.opening.variantsHint")}>
          <div className="flex flex-wrap items-center gap-2">
            <PillTabs tabs={variantTabs} value={String(at)} onChange={(v) => setVariantAt(Number(v))} small />
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 gap-1 px-2 text-xs"
              disabled={disabled || slotExtras.length >= OUTBOUND_TEXT_LIMITS.extraReasons}
              onClick={addVariant}
            >
              <Plus className="size-3.5" />
              {t("settings.aiOutbound.opening.addVariant")}
            </Button>
            {at > 0 ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 gap-1 px-2 text-xs"
                disabled={disabled}
                onClick={removeVariant}
              >
                <Trash2 className="size-3.5" />
                {t("settings.aiOutbound.opening.removeVariant")}
              </Button>
            ) : null}
          </div>
        </FieldBox>
      ) : null}

      <Field
        label={t("settings.aiOutbound.opening.reason")}
        hint={
          at === 0
            ? t(`${own}.reasonHint`, { length: reasonValue.length, max: OUTBOUND_TEXT_LIMITS.reason })
            : t("settings.aiOutbound.opening.variantHint", { length: reasonValue.length, max: OUTBOUND_TEXT_LIMITS.reason })
        }
      >
        <textarea
          className="crm-area min-h-[72px] text-[13px]"
          rows={2}
          maxLength={OUTBOUND_TEXT_LIMITS.reason}
          value={reasonValue}
          placeholder={at === 0 ? defaults.reasons[slot] : t("settings.aiOutbound.opening.variantPlaceholder")}
          disabled={disabled}
          onChange={(e) => setReason(e.target.value)}
        />
      </Field>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="crm-sub">{t("settings.aiOutbound.opening.insert")}</span>
        {OPENING_PLACEHOLDERS.map((placeholder) => (
          <Button
            key={placeholder}
            type="button"
            variant="outline"
            size="sm"
            className="h-7 px-2 font-mono text-xs"
            disabled={disabled}
            onClick={() => setReason(withPlaceholder(reasonValue, placeholder))}
          >
            {placeholder}
          </Button>
        ))}
      </div>
      {reasonValue.includes(FINDING_PLACEHOLDER) ? (
        <Note tone="info">{t("settings.aiOutbound.opening.findingNote")}</Note>
      ) : null}
      {reasonProblems.length > 0 ? (
        <Note tone="bad">{t("settings.aiOutbound.opening.problems", { problems: problemText(reasonProblems) })}</Note>
      ) : null}

      <Field
        label={t("settings.aiOutbound.opening.question")}
        hint={t("settings.aiOutbound.opening.questionHint", { max: OUTBOUND_TEXT_LIMITS.question })}
      >
        <Pill
          value={draft.question}
          placeholder={defaults.question}
          disabled={disabled}
          onChange={(question) => onChange({ ...draft, question })}
        />
      </Field>
      {questionProblems.length > 0 ? (
        <Note tone="bad">{t("settings.aiOutbound.opening.problems", { problems: problemText(questionProblems) })}</Note>
      ) : null}

      <div className="space-y-2 rounded-xl border border-border bg-card/60 p-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="crm-lab !mt-0">{t("settings.aiOutbound.opening.preview")}</span>
          {loading ? <Loader2 className="size-3.5 animate-spin text-muted-foreground" /> : null}
        </div>
        {serverAt < 0 ? (
          <p className="crm-sub text-xs">{t("settings.aiOutbound.opening.variantEmpty")}</p>
        ) : failed && !variant ? (
          <p className="crm-sub text-xs">{t("settings.aiOutbound.opening.previewFailed")}</p>
        ) : variant ? (
          <>
            <p className="text-[13.5px] leading-relaxed">{text}</p>
            <p className="crm-sub text-xs">
              {t("settings.aiOutbound.opening.length", { words: mobile ? variant.mobileWords : variant.words, seconds })}
            </p>
            {preview && seconds > preview.longSeconds ? (
              <p className="flex items-center gap-1.5 text-xs text-amber-700 dark:text-amber-400">
                <AlertTriangle className="size-3.5" />
                {t("settings.aiOutbound.opening.long", { seconds: preview.longSeconds })}
              </p>
            ) : null}
          </>
        ) : null}
        <ToggleRow
          label={t("settings.aiOutbound.opening.mobile")}
          hint={t("settings.aiOutbound.opening.mobileHint")}
          checked={mobile}
          onChange={setMobile}
        />
      </div>

      <Field
        label={t("settings.aiOutbound.opening.pitch")}
        hint={t("settings.aiOutbound.opening.pitchHint", { length: draft.pitch.length, max: OUTBOUND_TEXT_LIMITS.pitch })}
      >
        <textarea
          className="crm-area min-h-[96px] text-[13px]"
          rows={4}
          maxLength={OUTBOUND_TEXT_LIMITS.pitch}
          value={draft.pitch}
          placeholder={t("settings.aiOutbound.opening.pitchPlaceholder")}
          disabled={disabled}
          onChange={(e) => onChange({ ...draft, pitch: e.target.value })}
        />
      </Field>

      <FieldBox label={t(`${own}.goal`)} hint={t(`${own}.goalHint`)}>
        <SelectPill
          value={draft.goal}
          onChange={(goal) => onChange({ ...draft, goal: goal as OpeningDraft["goal"] })}
          label={t(`${own}.goal`)}
          disabled={disabled}
        >
          {OUTBOUND_GOALS.map((goal) => (
            <option key={goal} value={goal}>
              {t(`settings.aiOutbound.opening.goals.${goal}`)}
            </option>
          ))}
        </SelectPill>
      </FieldBox>
    </FieldGroup>
  );
}
